import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateRange, DateRangeConfig, DayOfWeek, getLabelForPeriod, isPresetPeriod } from '../../../../helpers/dateUtils';
import { AssociativeArray, EN_GB } from '../../../../helpers/helpers';
import useSavedField from '../../../../helpers/useSavedField';
import { SettingsContext } from '../../../../context/SettingsContext';
import { UserContext } from '../../../../context/UserContext';
import { DashboardFilters } from '../../../../models/dashboard';
import ValidationError from '../../../../models/validationError';
import ConfirmDiscardDialog from '../../../../components/ConfirmDiscardDialog';
import { TableChip } from '../../../../components/EnhancedTableToolbar';
import { DashboardContext } from '../../DashboardContext';
import { DEFAULT_BUSINESS_START_HOUR } from '../../../../constants';

export const DEFAULT_LOCATION_VALUE_KEY = "default";

export const defaultFiltersDummy: DashboardFilters = {
    fields: "",
    period: "today",
    locationArea: undefined,
    locations: [],
    comparisonOpen: false,
    comparePeriod: "yesterday"
};

const DashboardFiltersContext = React.createContext({
    filters: defaultFiltersDummy,
    unsavedFilters: defaultFiltersDummy,
    defaultFilters: defaultFiltersDummy,
    setFilters: (f: (prevFilters: DashboardFilters) => DashboardFilters) => { /* no op */
    },
    setFiltersStateless: (f: (prevFilters: DashboardFilters) => DashboardFilters) => { /* no op */
    },
    validateFilters: (() => false) as () => boolean,
    applyFilters: (() => false) as () => boolean,
    resetFilters: () => { /* no op */
    },
    forceSetFilters: (f: (prevState: DashboardFilters) => DashboardFilters) => { /* no op */
    },
    filtersChanged: false,
    filtersOpen: false,
    openFilters: () => { /* no  op */
    },
    closeFilters: (force?: boolean) => { /* no  op */
    },
    dateRangeConfig: {
        weeksStartOn: "monday" as DayOfWeek,
        daysStartAt: 0,
        useLocalTime: true
    } as DateRangeConfig,
    validationErrors: [] as ValidationError[],
    chips: [] as TableChip[],
    isLoading: true,
    isError: false,
    refetch: () => { /* no op */ },
});

/*
PSA for users of this context:
- We switched from a synchronous to an asynchronous paradigm due to the user preferences on when days and weeks start.
- This means this context now has isLoading, isError and refetch parameters, to handle the required API requests.
- While the filters context is loading, it is better to disregard the values of the context (as it will use defaults and update itself after loading, which will cause a visible flicker).
- So don't make requests until you have the filters loaded. And show to the user we are loading.
- If there is an error in the request, the context will show isError: true. You can call to refetch to make the filters grab the user preferences again.
- You may also just work with the defaults when the preferences fail to load, but this could cause user confusion.
 */
const DashboardFiltersContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { isLoadingCalendarPreferences, isErrorCalendarPreferences, calendarPreferences, refetchCalendarPreferences } = useContext(SettingsContext);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const { t } = useTranslation();
    const { user } = useContext(UserContext);
    const { companyConfig } = useContext(DashboardContext);

    const getLocationNameById = (id: string) => 
        companyConfig?.locations?.find(location => location.id === id)?.name || '';

    const periodToComparePeriodMap: AssociativeArray = {
        "today": "yesterday",
        "yesterday": "dayBeforeYesterday",
        "thisWeek": "lastWeek",
        "lastWeek": "weekBeforeLastWeek",
        "thisMonth": "lastMonth",
        "lastMonth": "monthBeforeLastMonth",
        "custom": "custom"
    }

    const dateRangeConfig: DateRangeConfig = useMemo(() => ({
        weeksStartOn: isLoadingCalendarPreferences ? "monday" : calendarPreferences.weeksStart as unknown as DayOfWeek,
        daysStartAt: isLoadingCalendarPreferences ? DEFAULT_BUSINESS_START_HOUR : calendarPreferences.daysStart,
        useLocalTime: true
    }), [isLoadingCalendarPreferences, calendarPreferences]);

    const defaultFilters: DashboardFilters = useMemo(() => ({
        period: "today",
        periodFrom: dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
        periodTo: dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
        locationArea: undefined,
        locations: [],
        comparisonOpen: false,
        comparePeriod: "yesterday",
        comparePeriodFrom: dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate,
        comparePeriodTo: dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate,
    }), [dateRangeConfig]);

    const {
        val: unsavedFilters,
        setVal: internalSetFilters,
        committed: filters,
        changed: filtersChanged,
        apply: internalApplyFilters,
        reset: internalResetFilters,
        forceSet: forceSetFilters
    } = useSavedField<DashboardFilters>(defaultFilters);

    // update default filters when user preferences are loaded
    useEffect(() => {
        forceSetFilters(() => defaultFilters);
    }, [forceSetFilters, defaultFilters]);

    const setFilters = useCallback((f: (prevFilters: DashboardFilters) => DashboardFilters) => {
        internalSetFilters((prevFilters) => {
            const newFilters = f(prevFilters);
            if (newFilters.period !== prevFilters.period && isPresetPeriod(newFilters.period)) {
                const range = dateRange(newFilters.period, undefined, undefined, undefined, dateRangeConfig);

                const hasNamedCustomCompare =
                    !!newFilters.comparePeriod &&
                    newFilters.comparePeriod !== "custom" &&
                    !isPresetPeriod(newFilters.comparePeriod);

                if (hasNamedCustomCompare) {
                    return {
                        ...newFilters,
                        periodFrom: range.startDate,
                        periodTo: range.endDate,
                    };
                }

                const auxComparePeriod = periodToComparePeriodMap[newFilters.period];
                const compareRange = dateRange(auxComparePeriod, undefined, undefined, undefined, dateRangeConfig);
                const comparePeriod = ["dayBeforeYesterday", "weekBeforeLastWeek", "monthBeforeLastMonth"].some(
                    (value) => value === auxComparePeriod) ? "custom" : auxComparePeriod;

                return {
                    ...newFilters,
                    periodFrom: range.startDate,
                    periodTo: range.endDate,
                    comparePeriod,
                    comparePeriodFrom: compareRange.startDate,
                    comparePeriodTo: compareRange.endDate
                }
            }
            if (
                newFilters.period === prevFilters.period &&
                (newFilters.periodFrom !== prevFilters.periodFrom || newFilters.periodTo !== prevFilters.periodTo)
            ) {
                return {
                    ...newFilters,
                    period: "custom"
                }
            }

            if(
                newFilters.comparePeriod &&
                (newFilters.comparePeriod !== prevFilters.comparePeriod && isPresetPeriod(newFilters.comparePeriod))
            ) {
                const range = dateRange(newFilters.comparePeriod, undefined, undefined, undefined, dateRangeConfig);
                return {
                    ...newFilters,
                    comparePeriodFrom: range.startDate,
                    comparePeriodTo: range.endDate
                }
            }

            if (
                newFilters.comparePeriod && 
                newFilters.comparePeriod === prevFilters.comparePeriod &&
                (newFilters.comparePeriodFrom !== prevFilters.comparePeriodFrom || newFilters.comparePeriodTo !== prevFilters.comparePeriodTo)
            ) {
                return {
                    ...newFilters,
                    comparePeriod: "custom"
                }
            }

            return newFilters;
        })
    }, [dateRangeConfig, internalSetFilters]);

    const validateFilters = useCallback(() => {
        const errors = [];

        // date checks
        // we must add 1 extra hour to account for a maximum date range
        // that includes a day where daylight savings happen, and 
        // consequently, 1 extra hour exists inside the range
        const maxDayLimitHours = 31 * 24 + 1;
        const validFrom = !Number.isNaN(Date.parse(unsavedFilters.periodFrom || ""));
        const validTo = !Number.isNaN(Date.parse(unsavedFilters.periodTo || ""));
        let dateGap;

        if (!validFrom) {
            errors.push({ field: "periodFrom", message: t("components.datePicker.enterValidDate") });
        }
        if (!validTo) {
            errors.push({ field: "periodTo", message: t("components.datePicker.enterValidDate") });
        }
        if (validFrom && validTo) {
            dateGap = new Date(unsavedFilters.periodTo || "").getTime() - new Date(unsavedFilters.periodFrom || "").getTime()
        }
        if (dateGap && dateGap < 0) {
            errors.push({ field: "periodFrom", message: t("components.datePicker.startDateMayNotBeAfterEnd") });
        }
        // Converts the filter's milliseconds to hours and compares to 31 days worth of hours 
        if (dateGap && (dateGap) / (1000 * 60 * 60) > maxDayLimitHours) {
            errors.push({ field: "periodTo", message: t("components.datePicker.dateRangeLimitedToOneMonth") });
        }

        setValidationErrors(errors);
        return errors.length === 0;
    }, [unsavedFilters, setValidationErrors, t]);

    const applyFilters = useCallback(() => {
        if (!validateFilters()) {
            return false;
        }
        setValidationErrors([]);
        internalApplyFilters();
        return true;
    }, [validateFilters, setValidationErrors, internalApplyFilters]);

    const resetFilters = useCallback(() => {
        setValidationErrors([]);
        internalResetFilters();
    }, [setValidationErrors, internalResetFilters]);

    const openFilters = useCallback(() => {
        setFiltersOpen(true);
    }, [setFiltersOpen]);

    const closeFilters = useCallback((force = false) => {
        if (filtersChanged && !force) {
            setDialogOpen(true);
            return;
        }
        setFiltersOpen(false);
    }, [setDialogOpen, setFiltersOpen, filtersChanged]);

    const resetPeriod = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            period: defaultFilters.period,
            periodFrom: defaultFilters.periodFrom,
            periodTo: defaultFilters.periodTo,
        }));
    }, [forceSetFilters, defaultFilters]);

    const resetComparePeriod = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            comparisonOpen: false,
            comparePeriod: defaultFilters.comparePeriod,
            comparePeriodFrom: defaultFilters.comparePeriodFrom,
            comparePeriodTo: defaultFilters.comparePeriodTo,
        }));
    }, [forceSetFilters, defaultFilters]);

    const resetLocation = useCallback(() => {
            forceSetFilters((prevFilters) => ({
                ...prevFilters,
                locations: [],
            }));
        }, [forceSetFilters]);

    const resetLocationArea = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            locationArea: undefined,
        }));
    }, [forceSetFilters]);

    const context = useMemo(() => {
        const chips = [
            {
                label: getLabelForPeriod(filters.period, filters.periodFrom, filters.periodTo, t, user?.locale || EN_GB),
                labelType: 'period',
                onDelete: filters.period === defaultFilters.period ? undefined : () => {
                    resetPeriod();
                    openFilters();
                }
            },
            ...(filters.locations && filters.locations.length ? [{
                label: `${t("screens.transactions.fields.location")}: ${filters.locations.length > 1 ?
                    `${getLocationNameById(filters.locations[0])}... (${filters.locations.length})` : `${getLocationNameById(filters.locations[0])}`
                }`,
                labelType: 'locations',
                onDelete: () => {
                    resetLocation();
                    openFilters();
                }
            }] : []),
            ...(filters.locationArea ? [{
                label: `${t("screens.transactions.fields.locationArea")}: ${filters.locationArea.name}`,
                labelType: 'locationArea',
                onDelete: () => {
                    resetLocationArea();
                    openFilters();
                }
            }] : []),
            ...(filters.comparisonOpen && filters.comparePeriod ? [{
                label: `${t("screens.dashboarding.fields.comparisonEnabled")}: 
                ${getLabelForPeriod(filters.comparePeriod, filters.comparePeriodFrom, filters.comparePeriodTo, t, user?.locale || EN_GB)}`,
                labelType: 'periodCompare',
                onDelete: () => {
                    if(filters.comparePeriod !== defaultFilters.comparePeriod) resetComparePeriod();
                    resetComparePeriod();
                    openFilters();
                }
            }] : []),
        ];
        return {
            filters,
            unsavedFilters,
            setFilters,
            setFiltersStateless: internalSetFilters,
            validateFilters,
            applyFilters,
            resetFilters,
            defaultFilters,
            filtersChanged,
            filtersOpen,
            openFilters,
            closeFilters,
            forceSetFilters,
            dateRangeConfig,
            validationErrors,
            chips,
            isLoading: isLoadingCalendarPreferences,
            isError: isErrorCalendarPreferences,
            refetch: refetchCalendarPreferences,
        };
    }, [t, isErrorCalendarPreferences, refetchCalendarPreferences, isLoadingCalendarPreferences, defaultFilters, filters, unsavedFilters, setFilters, internalSetFilters, validateFilters, applyFilters, resetFilters, filtersChanged, filtersOpen, openFilters, closeFilters, forceSetFilters, dateRangeConfig, validationErrors, resetPeriod]);

    return <><DashboardFiltersContext.Provider value={context}>{children}</DashboardFiltersContext.Provider>
        <ConfirmDiscardDialog 
            title={t('components.filters.confirmDiscard.title')}
            description={t('components.filters.confirmDiscard.message')}
            dialogOpen={dialogOpen} 
            onClose={() => setDialogOpen(false)} 
            onApply={() => {
                if (applyFilters()) setFiltersOpen(false);
            }}
            onReset={() => {
                resetFilters();
                setFiltersOpen(false);
            }} />
    </>;
}

export {
    DashboardFiltersContext,
    DashboardFiltersContextProvider
}
